import { DOCUMENT } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { OKTA_CONFIG } from '@okta/okta-angular';
import { OktaAuth } from '@okta/okta-auth-js';
import { firstValueFrom } from 'rxjs';
import { MessageService } from 'primeng/api';
import { environment } from 'src/environments/environment';
import { AuthService } from './auth.service';
import { clearAppSessionStorage } from './okta-logout';
import { TabTokenService } from './tab-token.service';

@Injectable({
  providedIn: 'root'
})
export class TabSessionService {
  private readonly authService = inject(AuthService);
  private readonly document = inject<Document>(DOCUMENT);
  private readonly http = inject(HttpClient);
  private readonly messageService = inject(MessageService);
  private readonly oktaConfig = inject(OKTA_CONFIG);
  private readonly router = inject(Router);
  private readonly tabTokenService = inject(TabTokenService);
  private logoutPromise?: Promise<boolean>;

  logout(): Promise<boolean> {
    if (this.logoutPromise) {
      return this.logoutPromise;
    }

    const oktaAuth: OktaAuth = this.oktaConfig.oktaAuth;

    this.logoutPromise = this.completeLogout(oktaAuth)
      .finally(() => {
        this.logoutPromise = undefined;
      });

    return this.logoutPromise;
  }

  private async completeLogout(oktaAuth: OktaAuth): Promise<boolean> {
    if (this.authService.hasLocalSession()) {
      this.authService.clearCurrentTabSession();
      clearAppSessionStorage();
      this.tabTokenService.releaseCurrentToken();
      return await this.router.navigate(['/security-login'], { replaceUrl: true });
    }

    try {
      await firstValueFrom(this.http.post<void>(`${environment.apiUrl}auth/logout`, null));
    } catch {
      this.messageService.add({
        key: 'session',
        severity: 'error',
        summary: 'Sign out failed',
        detail: 'You are still signed in. Please try again.'
      });
      return false;
    }

    this.authService.clearCurrentTabSession();
    clearAppSessionStorage();
    window.sessionStorage.setItem('force-okta-login', 'true');
    this.tabTokenService.releaseCurrentToken();

    let navigated: boolean;

    try {
      navigated = await this.router.navigate(['/signed-out'], { replaceUrl: true });
    } catch {
      this.forceCurrentTabToSignedOut(oktaAuth);
      return true;
    }

    if (!navigated) {
      this.forceCurrentTabToSignedOut(oktaAuth);
      return true;
    }

    oktaAuth.tokenManager.clear();

    try {
      await oktaAuth.authStateManager.updateAuthState();
    } catch {
      // The current tab is already cleared and on the public signed-out route.
    }

    return true;
  }

  private forceCurrentTabToSignedOut(oktaAuth: OktaAuth): void {
    oktaAuth.tokenManager.clear();
    this.document.location.replace('/signed-out');
  }
}
